import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useBackgroundTasks } from "../contexts/BackgroundTaskContext";

interface TasksDropdownProps {
    activeTasksCount: number;
    unviewedTasksCount: number;
    totalNotifications: number;
}

export const TasksDropdown: React.FC<TasksDropdownProps> = ({
    activeTasksCount,
    unviewedTasksCount,
    totalNotifications,
}) => {
    const navigate = useNavigate();
    const { tasks, clearCompletedTasks, clearTask, navigateToResult } =
        useBackgroundTasks();
    const [showTasksDropdown, setShowTasksDropdown] = useState(false);
    const tasksDropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                tasksDropdownRef.current &&
                !tasksDropdownRef.current.contains(event.target as Node)
            ) {
                setShowTasksDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleTaskClick = (task: any) => {
        navigateToResult(task.id, navigate);
        setShowTasksDropdown(false);
    };

    if (totalNotifications === 0) {
        return null;
    }

    return (
        <div className="relative" ref={tasksDropdownRef}>
            <button
                onClick={() => setShowTasksDropdown(!showTasksDropdown)}
                className="relative flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 focus:outline-none"
            >
                {activeTasksCount > 0 && (
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500 mr-2"></span>
                )}
                <span>
                    {totalNotifications} task{totalNotifications > 1 ? "s" : ""}
                </span>
                {unviewedTasksCount > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-blue-500 rounded-full">
                        {unviewedTasksCount}
                    </span>
                )}
            </button>

            {/* Tasks Dropdown */}
            {showTasksDropdown && (
                <div className="absolute right-0 mt-2 w-96 bg-white rounded-md shadow-lg py-1 z-50">
                    <div className="px-4 py-2 border-b border-gray-200 flex justify-between items-center">
                        <h3 className="text-sm font-semibold text-gray-900">
                            Tasks & Notifications
                        </h3>
                        {tasks.some(
                            (t) =>
                                t.status === "completed" ||
                                t.status === "failed"
                        ) && (
                            <button
                                onClick={() => clearCompletedTasks()}
                                className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                                title="Remove all completed and failed tasks"
                            >
                                Clear All Completed
                            </button>
                        )}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        {tasks.length === 0 ? (
                            <div className="px-4 py-8 text-center text-gray-500 text-sm">
                                No tasks running
                            </div>
                        ) : (
                            tasks.map((task) => (
                                <div
                                    key={task.id}
                                    className={`px-4 py-3 hover:bg-gray-50 ${
                                        !task.viewed &&
                                        task.status === "completed"
                                            ? "bg-blue-50"
                                            : ""
                                    }`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-gray-900">
                                                {task.message}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {task.status === "running" &&
                                                    "In progress..."}
                                                {task.status === "pending" &&
                                                    "Waiting..."}
                                                {task.status ===
                                                    "completed" && (
                                                    <span className="text-green-600">
                                                        ✓ Completed{" "}
                                                        {task.completedAt &&
                                                            new Date(
                                                                task.completedAt
                                                            ).toLocaleTimeString()}
                                                        {task.tokenUsage && (
                                                            <span className="text-gray-600 ml-2">
                                                                •{" "}
                                                                {task.tokenUsage.toLocaleString()}{" "}
                                                                tokens
                                                            </span>
                                                        )}
                                                    </span>
                                                )}
                                                {task.status === "failed" && (
                                                    <span className="text-red-600">
                                                        ✗ Failed
                                                        {task.tokenUsage && (
                                                            <span className="text-gray-600 ml-2">
                                                                •{" "}
                                                                {task.tokenUsage.toLocaleString()}{" "}
                                                                tokens
                                                            </span>
                                                        )}
                                                    </span>
                                                )}
                                            </p>

                                            {/* Action buttons for completed/failed tasks */}
                                            {(task.status === "completed" ||
                                                task.status === "failed") && (
                                                <div className="mt-2 flex gap-2">
                                                    {task.status ===
                                                        "completed" &&
                                                        (task.type ===
                                                            "meal-plan-generation" ||
                                                            task.type ===
                                                                "meal-plan-regeneration") &&
                                                        task.result && (
                                                            <button
                                                                onClick={() =>
                                                                    handleTaskClick(
                                                                        task
                                                                    )
                                                                }
                                                                className="text-xs bg-primary-500 hover:bg-primary-600 text-white px-3 py-1 rounded"
                                                            >
                                                                View Meal Plan
                                                            </button>
                                                        )}
                                                    <button
                                                        onClick={() =>
                                                            clearTask(task.id)
                                                        }
                                                        className="text-xs bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded"
                                                    >
                                                        Clear
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center ml-2">
                                            {task.status === "running" && (
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
                                            )}
                                            {!task.viewed &&
                                                task.status === "completed" && (
                                                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                                )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
